import { createSelector } from '@ngrx/store';

import { SortMeta } from 'primeng/api';

import { AppState } from '@core/state';
import { createChildSelectors } from '@util/lib/ngrx-child-selectors';

import { Grid } from '#types/grid';

import { initialState } from './grid.reducers';

export const selectGridState = (state: AppState) => state.grid;

export const { selectSaved, selectSidebar, selectPanels } = createChildSelectors(selectGridState, initialState);

export const { selectFilters, selectColumnSettings, selectExportData, selectSaveGrid, selectLoadGrid } = createChildSelectors(
  selectPanels,
  initialState.panels
);

export const selectSavedGridList = (id: string) =>
  createSelector(selectSaved, (saved) => {
    if (saved[id]) {
      return saved[id];
    } else {
      return null;
    }
  });

export const selectGrids = createSelector(selectGridState, (state) => state.grids);

export const selectGridIds = createSelector(selectGridState, (state) => {
  const grids: string[] = [];

  for (const [id, grid] of Object.entries(state.grids)) {
    if (grid !== null) {
      grids.push(id);
    }
  }

  return grids;
});

export const selectGrid = <T>(id: string) =>
  createSelector(selectGrids, (grids) => {
    if (grids[id]) {
      return grids[id] as Grid<T>;
    } else {
      return {
        availableColumns: [],
        data: [],
        defaultAvailableColumns: [],
        defaultFilters: {},
        defaultSelectedColumns: [],
        defaultSorts: {},
        filters: {},
        first: 1,
        initialized: false,
        page: 1,
        rows: 50,
        saveId: null,
        saveName: null,
        selectedColumns: [],
        selectedRow: null,
        selectedRows: null,
        sorts: {},
        summaryRow: null,
        summaryRowText: null,
        totalRows: 0,
      } as Grid<T>;
    }
  });

export const selectGridInitialized = (id: string) => createSelector(selectGrid(id), (grid: Grid) => grid.initialized);

export const selectGridFilters = (id: string) => createSelector(selectGrid(id), (grid: Grid) => grid.filters);
export const selectGridSortsObject = (id: string) => createSelector(selectGrid(id), (grid: Grid) => grid.sorts);

export const selectGridSorts = (id: string) =>
  createSelector(selectGrid(id), (grid: Grid) => {
    if (!grid || !grid.sorts) {
      return [];
    }

    const sortArray: SortMeta[] = [];

    for (const [field, sort] of Object.entries(grid.sorts)) {
      sortArray.push({
        field,
        order: sort.order,
      });
    }

    return sortArray;
  });

export const selectGridData = <T>(id: string) => createSelector(selectGrid<T>(id), (grid: Grid<T>) => grid.data);
export const selectGridSelectedRow = <T>(id: string) => createSelector(selectGrid<T>(id), (grid: Grid<T>) => grid.selectedRow);
export const selectGridSelectedRows = <T>(id: string) => createSelector(selectGrid<T>(id), (grid: Grid<T>) => grid.selectedRows);

export const selectGridSummaryRow = (id: string) => createSelector(selectGrid(id), (grid: Grid) => grid.summaryRow);
export const selectGridSummaryRowText = (id: string) => createSelector(selectGrid(id), (grid: Grid) => grid.summaryRowText);
export const selectGridAvailableColumns = (id: string) => createSelector(selectGrid(id), (grid: Grid) => grid.availableColumns);
export const selectGridSelectedColumns = (id: string) => createSelector(selectGrid(id), (grid: Grid) => grid.selectedColumns);

export const selectGridDefaultFilters = (id: string) => createSelector(selectGrid(id), (grid: Grid) => grid.defaultFilters);
export const selectGridDefaultSorts = (id: string) => createSelector(selectGrid(id), (grid: Grid) => grid.defaultSorts);
export const selectGridDefaultAvailableColumns = (id: string) =>
  createSelector(selectGrid(id), (grid: Grid) => grid.defaultAvailableColumns);
export const selectGridDefaultSelectedColumns = (id: string) => createSelector(selectGrid(id), (grid: Grid) => grid.defaultSelectedColumns);

export const selectGridFirst = (id: string) => createSelector(selectGrid(id), (grid: Grid) => grid.first ?? 1);
export const selectGridPage = (id: string) => createSelector(selectGrid(id), (grid: Grid) => grid.page ?? 1);
export const selectGridRows = (id: string) => createSelector(selectGrid(id), (grid: Grid) => grid.rows ?? 0);
export const selectGridRowsDisplayed = (id: string) => createSelector(selectGrid(id), (grid: Grid) => grid.data?.length ?? 0);
export const selectGridTotalRows = (id: string) => createSelector(selectGrid(id), (grid: Grid) => grid.totalRows ?? 0);

export const selectGridSaveId = (id: string) => createSelector(selectGrid(id), (grid: Grid) => grid.saveId);
export const selectGridSaveName = (id: string) => createSelector(selectGrid(id), (grid: Grid) => grid.saveName);
